import { SCREEN_PATTERNS } from '@base/shared';
import {
  Calendar,
  FileText,
  Images,
  KanbanSquare,
  Layers,
  LayoutDashboard,
  List,
  LogIn,
  MessageSquare,
  Monitor,
  Moon,
  Printer,
  SlidersHorizontal,
  Smartphone,
  Sun,
  Tablet,
  BarChart3,
  type LucideIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { localized } from '../i18n/index.ts';
import {
  PREVIEW_DEVICES,
  PREVIEW_READINGS,
  type PreviewDevice,
  type PreviewReading,
} from './Preview.tsx';
import { samplesFor } from './samples/index.ts';
import {
  PREVIEW_SCREENS,
  PREVIEW_STATES,
  type PreviewScreen,
  type PreviewState,
} from './screens/index.tsx';

const SCREEN_ICONS: Record<PreviewScreen, LucideIcon> = {
  dashboard: LayoutDashboard,
  list: List,
  record: FileText,
  board: KanbanSquare,
  agenda: Calendar,
  gallery: Images,
  wizard: Layers,
  conversation: MessageSquare,
  report: BarChart3,
  settings: SlidersHorizontal,
  document: Printer,
  login: LogIn,
};
const DEVICE_ICONS: Record<PreviewDevice, LucideIcon> = {
  desk: Monitor,
  tablet: Tablet,
  phone: Smartphone,
};

export interface PreviewView {
  screen: PreviewScreen;
  domain: string;
  state: PreviewState;
  reading: PreviewReading;
  device: PreviewDevice;
  dark: boolean;
}

/**
 * A barra em cima da prévia (ADR 0133, parte 3), como no protótipo: os 12 padrões de tela, o
 * exemplo de negócio, o estado da tela, a leitura, claro ou escuro e o aparelho.
 */
export function PreviewBar({
  view,
  onChange,
  hasDark,
  noColorHint,
  onPeekColor,
}: {
  view: PreviewView;
  onChange: (next: PreviewView) => void;
  hasDark: boolean;
  /** Na etapa Layout: avisa que está sem cor, com o atalho para ver com cor. */
  noColorHint?: boolean;
  onPeekColor?: () => void;
}) {
  const { t, i18n } = useTranslation();
  const set = (patch: Partial<PreviewView>) => onChange({ ...view, ...patch });
  const name = (id: string) => localized(SCREEN_PATTERNS.find((p) => p.id === id)?.name);
  const select = 'ui-control ui-input border px-2 text-sm';
  const toggle = (on: boolean) =>
    `flex size-8 items-center justify-center rounded-md ${on ? 'bg-sheet text-ink shadow-sm' : 'text-muted hover:text-ink'}`;
  return (
    <div className="flex flex-col gap-2">
      <div
        role="tablist"
        aria-label={t('style.bar.screens')}
        className="flex gap-1 overflow-x-auto pb-1"
      >
        {PREVIEW_SCREENS.map((s) => {
          const Icon = SCREEN_ICONS[s];
          const on = s === view.screen;
          return (
            <button
              key={s}
              type="button"
              role="tab"
              aria-selected={on}
              onClick={() => set({ screen: s })}
              className={`flex shrink-0 items-center gap-1.5 rounded-md px-3 py-1.5 text-sm whitespace-nowrap ${on ? 'bg-sheet font-medium text-ink shadow-sm' : 'text-muted hover:text-ink'}`}
            >
              <Icon size={15} aria-hidden />
              {name(s)}
            </button>
          );
        })}
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
        <label className="flex items-center gap-2">
          <span className="text-muted">{t('style.bar.example')}</span>
          <select
            className={select}
            value={view.domain}
            onChange={(e) => set({ domain: e.target.value })}
          >
            {samplesFor(i18n.language).map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2">
          <span className="text-muted">{t('style.bar.state')}</span>
          <select
            className={select}
            value={view.state}
            onChange={(e) => set({ state: e.target.value as PreviewState })}
          >
            {PREVIEW_STATES.map((s) => (
              <option key={s} value={s}>
                {t(`style.bar.states.${s}`)}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2">
          <span className="text-muted">{t('style.bar.reading')}</span>
          <select
            className={select}
            value={view.reading}
            onChange={(e) => set({ reading: e.target.value as PreviewReading })}
          >
            {PREVIEW_READINGS.map((r) => (
              <option key={r} value={r}>
                {t(`style.bar.readings.${r}`)}
              </option>
            ))}
          </select>
        </label>
        {hasDark && !noColorHint && (
          <span
            role="group"
            aria-label={t('style.bar.mode')}
            className="flex gap-0.5 rounded-lg bg-paper p-0.5"
          >
            <button
              type="button"
              aria-pressed={!view.dark}
              aria-label={t('style.bar.light')}
              title={t('style.bar.light')}
              className={toggle(!view.dark)}
              onClick={() => set({ dark: false })}
            >
              <Sun size={15} aria-hidden />
            </button>
            <button
              type="button"
              aria-pressed={view.dark}
              aria-label={t('style.bar.dark')}
              title={t('style.bar.dark')}
              className={toggle(view.dark)}
              onClick={() => set({ dark: true })}
            >
              <Moon size={15} aria-hidden />
            </button>
          </span>
        )}
        {noColorHint && (
          <span className="text-muted">
            {t('style.previewNoColor')}{' '}
            <button
              type="button"
              onClick={onPeekColor}
              className="text-brand-text underline-offset-2 hover:underline"
            >
              {t('style.bar.peekColor')}
            </button>
          </span>
        )}
        <span
          role="group"
          aria-label={t('style.bar.device')}
          className="ml-auto flex gap-0.5 rounded-lg bg-paper p-0.5"
        >
          {PREVIEW_DEVICES.map((dv) => {
            const Icon = DEVICE_ICONS[dv];
            return (
              <button
                key={dv}
                type="button"
                aria-pressed={view.device === dv}
                aria-label={t(`style.bar.devices.${dv}`)}
                title={t(`style.bar.devices.${dv}`)}
                onClick={() => set({ device: dv })}
                className={toggle(view.device === dv)}
              >
                <Icon size={15} aria-hidden />
              </button>
            );
          })}
        </span>
      </div>
    </div>
  );
}
